import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import { Theme } from '@mui/material/styles';
import { IDropshippingRequestProductSchema } from '../../../../../../../graphql/type/dropshippingRequestProduct';
import useRoutes from '../../../../../../components/_hook/useRoutes';
import { IValue } from '../../../../../../components/_select/_module/Select';
import SelectCurrency from '../../../../../../components/_select/SelectCurrency';
import useLanguage from '../../../../../../components/_hook/useLanguage';
import LabelDropshippingRequestProductStatus from '../../../../_module/LabelDropshippingRequestProductStatus';
import { IProductSchema } from '../../../../../../../graphql/type/product';
import SchemaProductPrice from '../../../../../../components/_schema/SchemaProductPrice';

interface IItem extends IDropshippingRequestProductSchema {
  key: string;
  variationId?: string;
  customPriceCurrency?: string;
  customPriceFinal?: number;
  customPriceByPricePercentage?: number;
  customPriceByDropshipperPricePercentage?: number;
  variation?: IProductSchema['variations'][0];
}

interface IProps {
  item: IItem;
  setTableRefreshKey?: (tableRefreshKey: number) => void;
  isEditable?: boolean;
  onChange?: (item: IItem) => void;
}

const useStyles = makeStyles<Theme>((theme) => ({
  containerSelectCurrency: {
    width: '120px',
    marginRight: theme.spacing(0.5),
  },
  typographyCurrentDisplayedPrice: { color: 'red' },
}));

export default function DropshippingDropshipperApprovedDetailTableItem(
  props: IProps
) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { item, setTableRefreshKey, isEditable, onChange } = props;
  const { path } = useRoutes();
  const { getText } = useLanguage();
  const [customPriceCurrency, setCustomPriceCurrency] = useState<string>(
    item.customPriceCurrency || ''
  );
  const [customPriceFinal, setCustomPriceFinal] = useState<number>(
    item.customPriceFinal || 0
  );

  useEffect(() => {
    setCustomPriceCurrency(item.customPriceCurrency || '');
    setCustomPriceFinal(item.customPriceFinal || 0);
  }, [JSON.stringify(item)]);

  if (!item.sourceProductData) {
    return <TableCell align="left">Source Product Not Found</TableCell>;
  }

  return (
    <>
      <TableCell align="left">
        <Tooltip title={item._id}>
          <Button
            color="primary"
            component={Link}
            to={path('product.information', {
              companyID: item.to_company_id,
              docID: item.source_product_id,
            })}
          >
            {getText(item.sourceProductData.name)}
            {item.variation && item.variation.attributes && (
              <>&nbsp;-&gt;&nbsp;{item.variation.attributes.sku}</>
            )}
            &nbsp;
          </Button>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <Typography
          variant="subtitle1"
          display="inline"
          className={classes.typographyCurrentDisplayedPrice}
        >
          {item.variation ? (
            <SchemaProductPrice
              product={item.sourceProductData}
              variationId={item.variationId}
            />
          ) : (
            <SchemaProductPrice product={item.sourceProductData} />
          )}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Grid container alignItems="center" justifyContent="center">
          {item.variation ? (
            <TextField
              disabled
              required
              value={
                item.variation.attributes && item.variation.attributes.prices
                  ? item.variation.attributes.prices
                  : ''
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {item.sourceProductData.prices &&
                    item.sourceProductData.prices.currency
                      ? item.sourceProductData.prices.currency
                      : ''}
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <TextField
              disabled
              required
              value={
                item.sourceProductData.prices &&
                item.sourceProductData.prices.final
                  ? item.sourceProductData.prices.final
                  : ''
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {item.sourceProductData.prices &&
                    item.sourceProductData.prices.currency
                      ? item.sourceProductData.prices.currency
                      : ''}
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Grid>
      </TableCell>
      <TableCell align="center">
        <Grid container alignItems="center" justifyContent="center">
          {item.variation ? (
            <TextField
              disabled
              required
              value={
                item.variation.dropshipping &&
                item.variation.dropshipping.prices &&
                item.variation.dropshipping.prices.final
                  ? item.variation.dropshipping.prices.final
                  : ''
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {item.sourceProductData.prices &&
                    item.sourceProductData.prices.currency
                      ? item.sourceProductData.prices.currency
                      : ''}
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <TextField
              disabled
              required
              value={
                item.sourceProductData.dropshipping &&
                item.sourceProductData.dropshipping.prices &&
                item.sourceProductData.dropshipping.prices.final
                  ? item.sourceProductData.dropshipping.prices.final
                  : ''
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {item.sourceProductData.prices &&
                    item.sourceProductData.prices.currency
                      ? item.sourceProductData.prices.currency
                      : ''}
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Grid>
      </TableCell>
      <TableCell align="center">
        <Grid container alignItems="center" justifyContent="center">
          {isEditable ? (
            <>
              {!item.variationId && (
                <div className={classes.containerSelectCurrency}>
                  <SelectCurrency<IValue>
                    disabled={!isEditable}
                    fullWidth
                    useShortLabel
                    menuPosition="fixed"
                    label=""
                    required
                    queryVariables={{
                      filter: {},
                    }}
                    value={customPriceCurrency}
                    onChange={(option) => {
                      const value = option ? option.value : '';
                      setCustomPriceCurrency(value);
                      if (onChange) {
                        onChange({
                          ...item,
                          ...{
                            customPriceCurrency: value,
                            customPriceFinal,
                          },
                        });
                      }
                    }}
                    placeholder={t('currency')}
                  />
                </div>
              )}
              <TextField
                disabled={!isEditable}
                type="number"
                required
                name="priceFinal"
                value={customPriceFinal}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  setCustomPriceFinal(value);
                  if (onChange) {
                    onChange({
                      ...item,
                      ...{
                        customPriceCurrency,
                        customPriceFinal: value,
                      },
                    });
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {item.variationId ? (
                        <>
                          {item.customPrices && item.customPrices.currency
                            ? item.customPrices.currency
                            : ''}
                        </>
                      ) : (
                        customPriceCurrency
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </>
          ) : (
            <TextField
              disabled
              required
              value={customPriceFinal}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {item.variationId ? (
                      <>
                        {item.customPrices && item.customPrices.currency
                          ? item.customPrices.currency
                          : ''}
                      </>
                    ) : (
                      customPriceCurrency
                    )}
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Grid>
      </TableCell>
      <TableCell align="center">
        <Grid container alignContent="center" justifyContent="center">
          <LabelDropshippingRequestProductStatus
            status={item.status}
            showIcon
            hideText
            iconSize="small"
          />
        </Grid>
      </TableCell>
    </>
  );
}
